import { useAlertContext } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { publicHolidaysApi } from "@/api";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export interface ImportHolidaysParams {
  fromYear: number;
  tillYear: number;
}

export function useHolidayImport() {
  const { t } = useTranslation();
  const { showSuccess, showInfo } = useAlertContext();
  const queryClient = useQueryClient();

  const { data: holidays, isLoading } = useQuery({
    queryKey: [QueryKey.PublicHolidays],
    queryFn: () => publicHolidaysApi.getPublicHolidays(),
  });

  const mutation = useMutation({
    mutationFn: (params: ImportHolidaysParams) =>
      publicHolidaysApi.importPublicHolidays({ importPublicHolidaysRequest: params }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.PublicHolidays] });

      // Some years may have no data at the source — surface them but treat the run as a success.
      if (result.skippedYears.length > 0) {
        showInfo(t("settings.holidays.importSuccessWithSkipped", { years: result.skippedYears.join(", ") }));
      } else {
        showSuccess(t("settings.holidays.importSuccess"));
      }
    },
    // Failures are surfaced by the global errorMiddleware, so no local onError is needed.
  });

  return {
    holidays: holidays ?? [],
    isLoading,
    triggerImport: mutation.mutate,
    isImporting: mutation.isPending,
  };
}
